import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Box } from '@mui/material';
import moment from 'moment-timezone';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { DateRangePreset, DateRangePresetId, DateRangeValue, isLastNHoursPreset } from './date-range-presets';
import {
  dateRangeLabelToNow,
  DateRangePresetMessageId,
  dateRangePresetMessages,
} from './date-range-selector-messages';

export interface DateRangeTriggerLabelProps {
  /** Current range (used to format custom range labels). */
  value?: DateRangeValue | null;
  /** Current preset (determines which label to show). */
  preset?: DateRangePreset | null;
  /** Preset used when preset is not provided. */
  defaultPreset?: DateRangePreset;
}

const DEFAULT_PRESET: DateRangePreset = 'today';

const dateFormatProps = {
  month: 'short' as const,
  day: 'numeric' as const,
  year: 'numeric' as const,
};

type DateRangeStringPresetMessageId = Exclude<DateRangePresetMessageId, 'lastNHours'>;

/**
 * Builds a compact custom range label.
 * When range starts/ends on the same day, avoids repeating the date in the end time.
 */
function formatCustomRangeLabel(
  value: DateRangeValue | null | undefined,
  getLabelByPresetId: (presetId: 'today' | 'yesterday') => string,
): string | null {
  if (!value?.from || !value?.to) {
    return null;
  }

  const from = moment(value.from);
  const to = moment(value.to);
  const now = moment();

  if (!from.isSame(to, 'day')) {
    return `${from.format('DD/MM HH:mm')} - ${to.format('DD/MM HH:mm')}`;
  }

  if (from.isSame(now, 'day')) {
    return `${getLabelByPresetId('today')} ${from.format('HH:mm')} - ${to.format('HH:mm')}`;
  }

  const yesterday = now.clone().subtract(1, 'day');
  if (from.isSame(yesterday, 'day')) {
    return `${getLabelByPresetId('yesterday')} ${from.format('HH:mm')} - ${to.format('HH:mm')}`;
  }

  return `${from.format('DD/MM HH:mm')} - ${to.format('HH:mm')}`;
}

function hasMessageLabelPreset(presetId: DateRangePresetId): presetId is DateRangeStringPresetMessageId {
  return presetId !== 'customRange';
}

/**
 * Renders the date range selector trigger content: calendar icon plus
 * a label derived from the current preset or custom range (e.g. "Hoy",
 * "14 de febrero de 2025 - 14 de febrero de 2025").
 * Uses FormattedDate / FormattedMessage from react-intl for locale-aware formatting.
 */
export function DateRangeTriggerLabel({
  value,
  preset,
  defaultPreset = DEFAULT_PRESET,
}: DateRangeTriggerLabelProps) {
  const { $t } = useIntl();
  const effectivePreset = preset ?? defaultPreset;
  const presetId: DateRangePresetId | null = typeof effectivePreset === 'string' ? effectivePreset : null;
  const customRangeLabel = formatCustomRangeLabel(value, (id) => $t(dateRangePresetMessages[id]));

  const label =
    presetId === 'customSingleDate' && value?.from ? (
      <FormattedDate value={value.from} {...dateFormatProps} />
    ) : presetId === 'fromCustomDateUntilNow' && value?.from ? (
      <FormattedMessage
        {...dateRangeLabelToNow}
        values={{
          from: <FormattedDate value={value.from} {...dateFormatProps} />,
        }}
      />
    ) : presetId === 'customFixedDateRange' && value?.from && value?.to ? (
      <>
        <FormattedDate value={value.from} {...dateFormatProps} />
        {' - '}
        <FormattedDate value={value.to} {...dateFormatProps} />
      </>
    ) : presetId === 'customRange' && customRangeLabel ? (
      customRangeLabel
    ) : isLastNHoursPreset(effectivePreset) ? (
      $t(dateRangePresetMessages.lastNHours, { hours: effectivePreset.hours })
    ) : presetId && hasMessageLabelPreset(presetId) ? (
      $t(dateRangePresetMessages[presetId])
    ) : (
      $t(dateRangePresetMessages.today)
    );

  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      <CalendarMonthIcon fontSize="small" />
      {label}
    </Box>
  );
}
